import {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type Ref,
} from 'react';
import { assignRef } from '../assign-ref.ts';
import { controlClassName, counterIdOf, describedByOf, Field, type FieldTexts } from '../Field/Field.tsx';
import styles from './Textarea.module.css';

export type TextareaProps = ComponentPropsWithRef<'textarea'> & FieldTexts;

const useLength = (ref: Ref<HTMLTextAreaElement> | undefined) => {
  const node = useRef<HTMLTextAreaElement | null>(null);
  const [length, setLength] = useState(0);
  const attach = useCallback(
    (element: HTMLTextAreaElement | null) => {
      node.current = element;
      assignRef(ref, element);
    },
    [ref],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    setLength(node.current?.value.length ?? 0);
  });
  return { length, setLength, attach };
};

export const Textarea = ({
  label,
  helperText,
  errorText,
  hideLabel,
  required,
  maxLength,
  rows = 3,
  id,
  ref,
  className,
  onChange,
  ...rest
}: TextareaProps) => {
  const generatedId = useId();
  const textareaId = id ?? generatedId;
  const { length, setLength, attach } = useLength(ref);
  const counter = maxLength === undefined ? undefined : `${String(length)} / ${String(maxLength)}`;
  const describedBy = [
    describedByOf(textareaId, { helperText, errorText }),
    counter ? counterIdOf(textareaId) : undefined,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Field
      id={textareaId}
      label={label}
      helperText={helperText}
      errorText={errorText}
      hideLabel={hideLabel}
      required={required}
      counter={counter}
    >
      <textarea
        id={textareaId}
        ref={attach}
        rows={rows}
        maxLength={maxLength}
        className={controlClassName(errorText, [styles['textarea'], className ?? ''].join(' '))}
        aria-invalid={errorText ? true : undefined}
        aria-describedby={describedBy || undefined}
        required={required}
        onChange={(event) => {
          setLength(event.target.value.length);
          onChange?.(event);
        }}
        {...rest}
      />
    </Field>
  );
};
